/**
 * PublicJobApplication.jsx — Şub 10, 2026
 *
 * Firma CNAME domain'i üzerinden yayınlanan public iş başvuru formu.
 * - Domain'e göre firma logosu ve adı dinamik çekilir (`/api/public/job-applications/firm`).
 * - Form gönderiminde `/api/public/job-applications` POST edilir.
 * - GDPR onayı zorunludur; CV (PDF/DOC/DOCX max 5MB) ve motivasyon mektubu opsiyonel.
 *
 * Tasarım: Fast-Trans HTML şablonuna sadık kalır (kart-bazlı, lacivert vurgu).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Globe, Loader2, Paperclip, Send } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// ───────────────────────── i18n (4 dil — kendi içinde) ─────────────────────────
const LANGS = [
  { code: 'tr', label: 'Türkçe',     flag: '\u{1F1F9}\u{1F1F7}' },
  { code: 'en', label: 'English',    flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'nl', label: 'Nederlands', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: 'fr', label: 'Français',   flag: '\u{1F1EB}\u{1F1F7}' },
];

const TX = {
  // Picker
  picker_brand_subtitle: { tr: 'İş Başvurusu',        en: 'Job Application',           nl: 'Sollicitatie',                fr: 'Candidature' },
  picker_title:          { tr: 'Hangi firmaya başvurmak istiyorsunuz?', en: 'Which company would you like to apply to?', nl: 'Welk bedrijf wilt u aansolliciteren?', fr: 'À quelle entreprise souhaitez-vous postuler ?' },
  picker_subtitle:       { tr: 'Bir firma seçin',     en: 'Choose a company',          nl: 'Kies een bedrijf',            fr: 'Choisissez une entreprise' },
  picker_footer:         { tr: "Firmanın kendi alan adı üzerinden de doğrudan başvurabilirsiniz.", en: "You can also apply directly via the company's own domain.", nl: "U kunt ook rechtstreeks via het eigen domein van het bedrijf solliciteren.", fr: "Vous pouvez aussi postuler directement via le domaine propre de l'entreprise." },

  // Header
  page_title:            { tr: 'Başvuru Formu',       en: 'Application Form',          nl: 'Sollicitatieformulier',       fr: 'Formulaire de candidature' },
  change_firm:           { tr: 'Firma değiştir',      en: 'Change company',            nl: 'Bedrijf wijzigen',            fr: "Changer d'entreprise" },

  // Sections
  sec_personal:          { tr: '1. Kişisel Bilgiler', en: '1. Personal details',       nl: '1. Persoonlijke gegevens',    fr: '1. Informations personnelles' },
  sec_position:          { tr: '2. Pozisyon & Müsaitlik', en: '2. Position & availability', nl: '2. Functie & beschikbaarheid', fr: '2. Poste & disponibilité' },
  sec_licenses:          { tr: '3. Ehliyetler & Sertifikalar', en: '3. Driving licenses & certificates', nl: '3. Rijbewijzen & certificaten', fr: '3. Permis & certificats' },
  sec_education:         { tr: '4. Eğitim & Deneyim', en: '4. Education & experience', nl: '4. Opleiding & ervaring',     fr: '4. Formation & expérience' },
  sec_documents:         { tr: '5. Belgeler',         en: '5. Documents',              nl: '5. Documenten',               fr: '5. Documents' },
  sec_source:            { tr: '6. Bizi nasıl buldunuz?', en: '6. How did you find us?', nl: '6. Hoe gevonden',          fr: '6. Comment nous avez-vous trouvé ?' },

  // Fields
  f_firstname:           { tr: 'Ad',                  en: 'First name',                nl: 'Voornaam',                    fr: 'Prénom' },
  f_lastname:            { tr: 'Soyad',               en: 'Last name',                 nl: 'Achternaam',                  fr: 'Nom' },
  f_email:               { tr: 'E-posta adresi',      en: 'E-mail address',            nl: 'E-mailadres',                 fr: 'Adresse e-mail' },
  f_phone:               { tr: 'Telefon numarası',    en: 'Phone number',              nl: 'Telefoonnummer',              fr: 'Numéro de téléphone' },
  f_birth:               { tr: 'Doğum tarihi',        en: 'Date of birth',             nl: 'Geboortedatum',               fr: 'Date de naissance' },
  f_nationality:         { tr: 'Uyruk',               en: 'Nationality',               nl: 'Nationaliteit',               fr: 'Nationalité' },
  f_city:                { tr: 'Yaşadığınız şehir',   en: 'City of residence',         nl: 'Woonplaats',                  fr: 'Ville de résidence' },
  f_city_ph:             { tr: 'Şehir, ülke',         en: 'City, country',             nl: 'Stad, land',                  fr: 'Ville, pays' },
  f_position:            { tr: 'Başvurulan pozisyon', en: 'Desired position',          nl: 'Gewenste functie',            fr: 'Poste souhaité' },
  f_position_ph:         { tr: 'Pozisyon seçin…',     en: 'Select a position…',        nl: 'Selecteer functie…',          fr: 'Sélectionnez un poste…' },
  f_available_from:      { tr: 'Müsait olduğunuz tarih', en: 'Available from',          nl: 'Beschikbaar vanaf',          fr: 'Disponible à partir du' },
  f_diploma:             { tr: 'En yüksek diploma',   en: 'Highest diploma',           nl: 'Hoogst behaalde diploma',     fr: 'Plus haut diplôme' },
  f_select_ph:           { tr: 'Seçin…',              en: 'Select…',                   nl: 'Selecteer…',                  fr: 'Sélectionnez…' },
  f_experience:          { tr: 'Konuyla ilgili deneyim (yıl)', en: 'Years of relevant experience', nl: 'Jaren relevante werkervaring', fr: "Années d'expérience pertinente" },
  f_employers:           { tr: 'Önceki işverenler',   en: 'Previous employers',        nl: 'Eerdere werkgevers',          fr: 'Employeurs précédents' },
  f_employers_ph:        { tr: 'Önceki işverenleri ve görevlerinizi yazın', en: 'List previous employers and roles', nl: 'Vermeld uw vorige werkgevers en functies', fr: 'Indiquez vos anciens employeurs et fonctions' },
  f_languages:           { tr: 'Diller (konuşma/yazma)', en: 'Languages (speaking/writing)', nl: 'Talen (spreek-/schrijfvaardigheid)', fr: 'Langues (parlées/écrites)' },
  f_motivation:          { tr: 'Kısa motivasyon',     en: 'Short motivation',          nl: 'Korte motivatie',             fr: 'Brève motivation' },
  f_motivation_ph:       { tr: 'Neden bizimle çalışmak istiyorsunuz?', en: 'Why do you want to work with us?', nl: 'Waarom wilt u bij ons werken?', fr: 'Pourquoi voulez-vous travailler avec nous ?' },
  f_cv:                  { tr: 'CV (pdf, doc, docx — maks. 5 MB)', en: 'CV (pdf, doc, docx — max 5 MB)', nl: 'CV (pdf, doc, docx — max. 5 MB)', fr: 'CV (pdf, doc, docx — max 5 Mo)' },
  f_brief:               { tr: 'Motivasyon mektubu (opsiyonel)', en: 'Cover letter (optional)', nl: 'Motivatiebrief (optioneel)', fr: 'Lettre de motivation (optionnelle)' },
  f_pick_file:           { tr: 'Bir dosya seçin…',    en: 'Choose a file…',            nl: 'Selecteer een bestand…',      fr: 'Choisir un fichier…' },

  // GDPR
  gdpr_text:             {
    tr: 'Kişisel verilerimin işe alım amaçları için <strong>{firm}</strong> tarafından işlenmesine onay veriyorum. Veriler en fazla 12 ay saklanır; talebiniz üzerine silinir.',
    en: 'I consent to the processing of my personal data for recruitment purposes by <strong>{firm}</strong>. Data is kept for a maximum of 12 months, unless agreed otherwise. We can delete your data on request.',
    nl: 'Ik ga akkoord met de verwerking van mijn persoonlijke gegevens voor wervingsdoeleinden door <strong>{firm}</strong>. Gegevens worden maximaal 12 maanden bewaard, tenzij anders overeengekomen. Op uw verzoek kunnen wij uw gegevens verwijderen.',
    fr: "Je consens au traitement de mes données personnelles à des fins de recrutement par <strong>{firm}</strong>. Les données sont conservées 12 mois maximum, sauf accord contraire. Nous pouvons supprimer vos données sur demande.",
  },

  // Submit & footer
  required_note:         { tr: '* işaretli alanlar zorunludur', en: 'Fields marked with * are required', nl: 'Velden met * zijn verplicht', fr: 'Les champs marqués * sont obligatoires' },
  submit:                { tr: 'Başvuruyu Gönder',    en: 'Send application',          nl: 'Sollicitatie verzenden',      fr: "Envoyer la candidature" },
  sending:               { tr: 'Gönderiliyor…',       en: 'Sending…',                  nl: 'Verzenden…',                  fr: 'Envoi…' },

  // Success
  success_title:         { tr: 'Başvurunuz alındı',   en: 'Application received',      nl: 'Sollicitatie verzonden',      fr: 'Candidature reçue' },
  success_body:          {
    tr: 'Başvurunuz için teşekkür ederiz <strong>{firm}</strong>. En kısa sürede sizinle iletişime geçeceğiz.',
    en: 'Thank you for applying to <strong>{firm}</strong>. We will get in touch with you as soon as possible.',
    nl: 'Bedankt voor uw sollicitatie bij <strong>{firm}</strong>. We nemen zo snel mogelijk contact met u op.',
    fr: 'Merci pour votre candidature chez <strong>{firm}</strong>. Nous vous contacterons dès que possible.',
  },
  new_application:       { tr: 'Yeni başvuru',        en: 'New application',           nl: 'Nieuwe sollicitatie',         fr: 'Nouvelle candidature' },

  // Errors
  err_gdpr:              { tr: 'GDPR onayı gereklidir',         en: 'GDPR consent is required',     nl: 'GDPR-toestemming is vereist',   fr: 'Le consentement RGPD est requis' },
  err_required:          { tr: 'Lütfen zorunlu alanları doldurun', en: 'Please fill required fields', nl: 'Vul de verplichte velden in', fr: 'Veuillez remplir les champs requis' },
  err_submit:            { tr: 'Gönderim başarısız',  en: 'Submission failed',         nl: 'Verzenden mislukt',           fr: 'Échec de l’envoi' },
  err_file_size:         { tr: '{name} 5MB sınırını aşıyor', en: '{name} exceeds the 5MB limit', nl: '{name} overschrijdt de limiet van 5MB', fr: '{name} dépasse la limite de 5 Mo' },
};

const detectBrowserLang = () => {
  try {
    const saved = localStorage.getItem('jobapp_lang');
    if (saved && LANGS.find((l) => l.code === saved)) return saved;
  } catch { /* ignore */ }
  const nav = (typeof navigator !== 'undefined' && navigator.language) || 'en';
  const code = nav.split('-')[0].toLowerCase();
  return LANGS.find((l) => l.code === code) ? code : 'en';
};

const tx = (key, lang, vars = {}) => {
  const dict = TX[key] || {};
  let s = dict[lang] || dict.en || dict.nl || key;
  Object.keys(vars).forEach((k) => {
    s = s.replaceAll(`{${k}}`, vars[k] ?? '');
  });
  return s;
};

const FUNCTIES = [
  'Koerier',
  'Vrachtwagenchauffeur (nationaal)',
  'Vrachtwagenchauffeur (internationaal)',
];
const DIPLOMAS = [
  'Geen diploma',
  'Lager onderwijs',
  'Middelbaar onderwijs',
  'Bachelor',
  'Master',
  'Doctoraat',
];
const ERVARING = ['Geen', '1-2 jaar', '3-5 jaar', '6-10 jaar', '10+ jaar'];
const RIJBEWIJZEN = [
  'Rijbewijs B',
  'Rijbewijs C (vrachtwagen)',
  'Rijbewijs CE (vrachtwagen + aanhanger)',
  'Bestuurderskaart (digitale tachograaf)',
  'ADR-certificaat (gevaarlijke goederen)',
  'Heftruck / reachtruck attest',
  'Vakbekwaamheidscode (code 95)',
];
const TALEN = ['Nederlands', 'Frans', 'Engels', 'Duits', 'Turks', 'Andere'];
const BRONNEN = ['Website', 'LinkedIn', 'Indeed / Jobat', 'Via een kennis', 'Andere'];
const MAX_FILE = 5 * 1024 * 1024;

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const initialForm = {
  voornaam: '', achternaam: '', email: '', telefoon: '',
  geboortedatum: '', nationaliteit: '', woonplaats: '',
  functie: '', beschikbaar: '',
  rijbewijs: [],
  diploma: '', ervaring: '', werkgevers: '', talen: [],
  motivatie: '', bron: '', gdpr: false,
};

export default function PublicJobApplication() {
  const [lang, setLang] = useState(detectBrowserLang);
  const t = useMemo(() => (k, vars) => tx(k, lang, vars), [lang]);
  const changeLang = (code) => {
    setLang(code);
    try { localStorage.setItem('jobapp_lang', code); } catch { /* ignore */ }
  };

  const [firm, setFirm] = useState(null);
  const [loadingFirm, setLoadingFirm] = useState(true);
  // Şub 10, 2026 — Ana domain üzerinden açıldığında firma seçici devreye girer
  const [firmsList, setFirmsList] = useState([]);
  const [pickedFirmId, setPickedFirmId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [cvFile, setCvFile] = useState(null);
  const [briefFile, setBriefFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const cvRef = useRef(null);
  const briefRef = useRef(null);

  // Domain'e göre firma çek (firma yoksa picker akışına geç)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/public/job-applications/firm');
        if (r.data?.success && r.data.data?.firm_id) {
          setFirm(r.data.data);
        } else {
          setFirm(null);
          try {
            const lr = await api.get('/public/job-applications/firms');
            if (lr.data?.success) setFirmsList(lr.data.data || []);
          } catch { /* ignore */ }
        }
      } catch {
        setFirm(null);
      } finally {
        setLoadingFirm(false);
      }
    })();
  }, []);

  // Picker'dan firma seçildiğinde detayını al
  const selectFirm = async (f) => {
    setPickedFirmId(f.id);
    try {
      const r = await api.get(`/public/job-applications/firm?firm_id=${encodeURIComponent(f.id)}`);
      if (r.data?.success) {
        setFirm(r.data.data);
      } else {
        toast.error('Firma yüklenemedi');
      }
    } catch {
      toast.error('Firma yüklenemedi');
    }
  };

  const backToPicker = () => {
    setFirm(null);
    setPickedFirmId(null);
    setForm(initialForm);
    setCvFile(null);
    setBriefFile(null);
  };

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleList = (k, value) =>
    setForm((p) => {
      const cur = new Set(p[k] || []);
      if (cur.has(value)) cur.delete(value); else cur.add(value);
      return { ...p, [k]: Array.from(cur) };
    });

  const onFile = (kind, e) => {
    const f = e.target.files?.[0];
    if (!f) { kind === 'cv' ? setCvFile(null) : setBriefFile(null); return; }
    if (f.size > MAX_FILE) { toast.error(t('err_file_size', { name: f.name })); e.target.value = ''; return; }
    kind === 'cv' ? setCvFile(f) : setBriefFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr) { toast.error(t('err_gdpr')); return; }
    if (!form.voornaam || !form.achternaam || !form.email || !form.telefoon || !form.functie || !form.motivatie) {
      toast.error(t('err_required'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (cvFile) {
        payload.cv = await fileToDataUrl(cvFile);
        payload.cv_filename = cvFile.name;
      }
      if (briefFile) {
        payload.motivatiebrief = await fileToDataUrl(briefFile);
        payload.motivatiebrief_filename = briefFile.name;
      }
      // Picker akışında firm_id payload'a eklenir (host eşleşmiyorsa backend
      // bu alandan firmayı çözer).
      if (pickedFirmId) payload.firm_id = pickedFirmId;
      else if (firm?.firm_id) payload.firm_id = firm.firm_id;
      // Aday tarayıcı dili — backend confirmation e-postasını bu dilde gönderir
      payload.lang = lang;
      const r = await api.post('/public/job-applications', payload);
      if (r.data?.success) {
        setSubmitted(true);
        setForm(initialForm);
        setCvFile(null);
        setBriefFile(null);
        if (cvRef.current) cvRef.current.value = '';
        if (briefRef.current) briefRef.current.value = '';
      } else {
        toast.error(r.data?.message || t('err_submit'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('err_submit'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingFirm) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-700" />
      </div>
    );
  }

  if (!firm?.firm_id) {
    // Picker akışı — ana domain üzerinde gelindiyse OMS Portal + 3 firma logosu
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-end mb-4">
            <LangSwitcher lang={lang} onChange={changeLang} />
          </div>
        </div>
        <div className="max-w-3xl mx-auto flex items-center justify-center" data-testid="job-application-picker">
          <div className="w-full">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black text-xl shadow-lg">
                  OMS
                </div>
                <div className="text-left">
                  <h1 className="text-2xl font-bold text-slate-900">OMS Portal</h1>
                  <p className="text-xs text-slate-500">{t('picker_brand_subtitle')}</p>
                </div>
              </div>
              <h2 className="text-lg font-semibold text-slate-800 mt-4">
                {t('picker_title')}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {t('picker_subtitle')}
              </p>
            </div>

            {firmsList.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {firmsList.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => selectFirm(f)}
                    className="group bg-white border border-slate-200 hover:border-blue-500 hover:shadow-lg rounded-2xl p-6 text-center transition-all hover:-translate-y-0.5"
                    data-testid={`firm-pick-${f.id}`}
                  >
                    {f.logo_url ? (
                      <img
                        src={f.logo_url}
                        alt={f.name}
                        className="h-20 w-20 mx-auto object-contain rounded-xl bg-slate-50 p-2 mb-3"
                      />
                    ) : (
                      <div
                        className="h-20 w-20 mx-auto rounded-xl flex items-center justify-center text-white text-2xl font-black mb-3"
                        style={{ background: f.primary_color || '#1A3A6B' }}
                      >
                        {(f.name || '?').charAt(0)}
                      </div>
                    )}
                    <p className="font-bold text-slate-900 group-hover:text-blue-700 transition">
                      {f.name}
                    </p>
                    {f.domain && (
                      <p className="text-[11px] text-slate-400 mt-1">{f.domain}</p>
                    )}
                  </button>
                ))}
              </div>
            )}

            <p className="text-center text-xs text-slate-400 mt-8">
              {t('picker_footer')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const accent = firm.primary_color || '#1A3A6B';

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div
          className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-10 text-center"
          data-testid="job-application-success"
        >
          <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-600 mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('success_title')}</h2>
          <p
            className="text-slate-600 mb-6"
            dangerouslySetInnerHTML={{ __html: t('success_body', { firm: firm.firm_name }) }}
          />
          <button
            onClick={() => setSubmitted(false)}
            className="px-5 py-2 rounded-lg text-white font-semibold"
            style={{ background: accent }}
            data-testid="job-application-new-btn"
          >
            {t('new_application')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-job-application">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8 px-2">
          {firm.logo_url ? (
            <img
              src={firm.logo_url}
              alt={firm.firm_name}
              className="h-16 w-16 rounded-xl object-contain bg-white border border-slate-200 p-2"
            />
          ) : (
            <div
              className="h-16 w-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
              style={{ background: accent }}
            >
              {(firm.firm_name || '?').charAt(0)}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: accent }}>
              {t('page_title')}
            </h1>
            <p className="text-sm text-slate-600">{firm.firm_name}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {pickedFirmId && (
              <button
                type="button"
                onClick={backToPicker}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100"
                data-testid="ja-change-firm"
              >
                {t('change_firm')}
              </button>
            )}
            <LangSwitcher lang={lang} onChange={changeLang} />
          </div>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="job-application-form">
          {/* 1. Persoonlijke gegevens */}
          <Card title={t('sec_personal')} accent={accent}>
            <Grid2>
              <Field label={t('f_firstname')} required>
                <input
                  type="text" required className={inputCls}
                  value={form.voornaam} onChange={(e) => update('voornaam', e.target.value)}
                  data-testid="ja-voornaam"
                />
              </Field>
              <Field label={t('f_lastname')} required>
                <input
                  type="text" required className={inputCls}
                  value={form.achternaam} onChange={(e) => update('achternaam', e.target.value)}
                  data-testid="ja-achternaam"
                />
              </Field>
              <Field label={t('f_email')} required>
                <input
                  type="email" required className={inputCls}
                  value={form.email} onChange={(e) => update('email', e.target.value)}
                  data-testid="ja-email"
                />
              </Field>
              <Field label={t('f_phone')} required>
                <input
                  type="tel" required className={inputCls}
                  value={form.telefoon} onChange={(e) => update('telefoon', e.target.value)}
                  data-testid="ja-telefoon"
                />
              </Field>
              <Field label={t('f_birth')}>
                <input
                  type="date" className={inputCls}
                  value={form.geboortedatum} onChange={(e) => update('geboortedatum', e.target.value)}
                  data-testid="ja-geboortedatum"
                />
              </Field>
              <Field label={t('f_nationality')}>
                <input
                  type="text" className={inputCls}
                  value={form.nationaliteit} onChange={(e) => update('nationaliteit', e.target.value)}
                  data-testid="ja-nationaliteit"
                />
              </Field>
              <Field label={t('f_city')} span="2">
                <input
                  type="text" className={inputCls}
                  placeholder={t('f_city_ph')}
                  value={form.woonplaats} onChange={(e) => update('woonplaats', e.target.value)}
                  data-testid="ja-woonplaats"
                />
              </Field>
            </Grid2>
          </Card>

          {/* 2. Functie & beschikbaarheid */}
          <Card title={t('sec_position')} accent={accent}>
            <Grid2>
              <Field label={t('f_position')} required>
                <select
                  required className={inputCls}
                  value={form.functie} onChange={(e) => update('functie', e.target.value)}
                  data-testid="ja-functie"
                >
                  <option value="">{t('f_position_ph')}</option>
                  {FUNCTIES.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label={t('f_available_from')}>
                <input
                  type="date" className={inputCls}
                  value={form.beschikbaar} onChange={(e) => update('beschikbaar', e.target.value)}
                  data-testid="ja-beschikbaar"
                />
              </Field>
            </Grid2>
          </Card>

          {/* 3. Rijbewijzen & certificaten */}
          <Card title={t('sec_licenses')} accent={accent}>
            <Pills options={RIJBEWIJZEN} selected={form.rijbewijs}
                   onToggle={(v) => toggleList('rijbewijs', v)} testid="ja-rijbewijs" />
          </Card>

          {/* 4. Opleiding & ervaring */}
          <Card title={t('sec_education')} accent={accent}>
            <Grid2>
              <Field label={t('f_diploma')}>
                <select
                  className={inputCls}
                  value={form.diploma} onChange={(e) => update('diploma', e.target.value)}
                  data-testid="ja-diploma"
                >
                  <option value="">{t('f_select_ph')}</option>
                  {DIPLOMAS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
              <Field label={t('f_experience')}>
                <div className="flex flex-wrap gap-2 pt-1">
                  {ERVARING.map((opt) => (
                    <button
                      type="button" key={opt}
                      onClick={() => update('ervaring', opt)}
                      className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${
                        form.ervaring === opt
                          ? 'text-white border-transparent'
                          : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                      }`}
                      style={form.ervaring === opt ? { background: accent } : {}}
                      data-testid={`ja-ervaring-${opt}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label={t('f_employers')} span="2">
                <textarea
                  rows={3} className={inputCls}
                  placeholder={t('f_employers_ph')}
                  value={form.werkgevers} onChange={(e) => update('werkgevers', e.target.value)}
                  data-testid="ja-werkgevers"
                />
              </Field>
              <Field label={t('f_languages')} span="2">
                <Pills options={TALEN} selected={form.talen}
                       onToggle={(v) => toggleList('talen', v)} testid="ja-talen" />
              </Field>
              <Field label={t('f_motivation')} required span="2">
                <textarea
                  rows={4} required className={inputCls}
                  placeholder={t('f_motivation_ph')}
                  value={form.motivatie} onChange={(e) => update('motivatie', e.target.value)}
                  data-testid="ja-motivatie"
                />
              </Field>
            </Grid2>
          </Card>

          {/* 5. Documenten */}
          <Card title={t('sec_documents')} accent={accent}>
            <Grid2>
              <Field label={t('f_cv')}>
                <label className="flex items-center gap-2 px-3 py-2.5 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 text-sm">
                  <Paperclip className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-700">{cvFile?.name || t('f_pick_file')}</span>
                  <input
                    ref={cvRef} type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => onFile('cv', e)}
                    className="hidden" data-testid="ja-cv"
                  />
                </label>
              </Field>
              <Field label={t('f_brief')}>
                <label className="flex items-center gap-2 px-3 py-2.5 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 text-sm">
                  <Paperclip className="h-4 w-4 text-slate-500" />
                  <span className="text-slate-700">{briefFile?.name || t('f_pick_file')}</span>
                  <input
                    ref={briefRef} type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => onFile('motivatiebrief', e)}
                    className="hidden" data-testid="ja-motivatiebrief"
                  />
                </label>
              </Field>
            </Grid2>
          </Card>

          {/* 6. Hoe gevonden */}
          <Card title={t('sec_source')} accent={accent}>
            <div className="flex flex-wrap gap-2">
              {BRONNEN.map((b) => (
                <button
                  type="button" key={b}
                  onClick={() => update('bron', b)}
                  className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${
                    form.bron === b
                      ? 'text-white border-transparent'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                  }`}
                  style={form.bron === b ? { background: accent } : {}}
                  data-testid={`ja-bron-${b}`}
                >
                  {b}
                </button>
              ))}
            </div>
          </Card>

          {/* GDPR */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <input
              id="gdpr" type="checkbox" required
              checked={form.gdpr}
              onChange={(e) => update('gdpr', e.target.checked)}
              className="mt-1 h-4 w-4"
              data-testid="ja-gdpr"
            />
            <label
              htmlFor="gdpr"
              className="text-xs text-slate-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: t('gdpr_text', { firm: firm.firm_name }) }}
            />
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
            <p className="text-xs text-slate-500">
              {t('required_note').replace('*', '')}
              <span className="text-red-600 font-semibold mx-1">*</span>
            </p>
            <button
              type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold shadow-md hover:shadow-lg transition disabled:opacity-60"
              style={{ background: accent }}
              data-testid="ja-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {submitting ? t('sending') : t('submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── helpers ── */
const inputCls =
  'w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

function Card({ title, children, accent }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6">
      <h2 className="text-base font-bold mb-4" style={{ color: accent }}>{title}</h2>
      {children}
    </div>
  );
}

function Grid2({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, span, children }) {
  return (
    <div className={span === '2' ? 'sm:col-span-2' : ''}>
      <label className="block text-xs font-semibold text-slate-700 mb-1">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      {children}
    </div>
  );
}

function Pills({ options, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isOn = selected.includes(opt);
        return (
          <button
            type="button" key={opt}
            onClick={() => onToggle(opt)}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${
              isOn
                ? 'bg-blue-700 text-white border-blue-700'
                : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
            }`}
            data-testid={`${testid}-${opt}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function LangSwitcher({ lang, onChange }) {
  const [open, setOpen] = useState(false);
  const current = LANGS.find((l) => l.code === lang) || LANGS[1];
  return (
    <div className="relative" data-testid="ja-lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-200 shadow-sm text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        <Globe className="h-3.5 w-3.5 text-slate-500" />
        <span>{current.flag}</span>
        <span>{current.label}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 overflow-hidden min-w-[150px]">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(l.code); setOpen(false); }}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-50 ${
                l.code === lang ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700'
              }`}
              data-testid={`ja-lang-${l.code}`}
            >
              <span>{l.flag}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
